import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, type MembershipTiersResponse, type MembershipTierDistribution } from '@/lib/adminApi';
import { Card, Spinner, EmptyState, Badge } from '@/components/ui';
import { AdminPage, AdminPageHeader } from '@/components/admin/shared';
import { useAdminPermission } from '@/lib/useAdminPermission';

/** Customer membership tiers — the free, automatic Silver/Gold/Platinum-Black
 *  ladder. A rider's tier is earned by km CONSUMED on trips over a rolling 6
 *  months; the tier grants a % discount on km-wallet top-ups. The thresholds +
 *  percentages live in the singleton `membership_tier_settings` row (admin-svc
 *  owns it; wallet-svc reads it live, so an edit here takes effect immediately —
 *  no redeploy). Edit needs cms.manage; degrades read-only if the migration
 *  isn't applied yet. The cards up top show the live member distribution. */
export function MembershipTiers() {
  const canManage = useAdminPermission('cms.manage');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'membership-tiers'],
    queryFn: ({ signal }) => adminApi.membershipTiers(signal),
  });

  const available = data?.available !== false;

  return (
    <AdminPage>
      <AdminPageHeader
        title="Membership Tiers"
        subtitle="The customer Silver / Gold / Platinum Black ladder — km consumed over a rolling 6 months earns a discount on km-wallet top-ups."
        right={
          data ? (
            <Badge tone={!available ? 'neutral' : data.enabled ? 'ok' : 'warn'}>
              {!available ? 'not configured' : data.enabled ? 'live' : 'disabled'}
            </Badge>
          ) : undefined
        }
      />

      {isLoading ? (
        <Card>
          <div className="flex items-center justify-center py-12">
            <Spinner label="Loading tiers…" />
          </div>
        </Card>
      ) : isError ? (
        <Card>
          <EmptyState title="Couldn't load tiers" description={(error as Error)?.message} />
        </Card>
      ) : !available ? (
        <Card>
          <EmptyState
            title="Not configured yet"
            description="The membership_tier_settings table isn’t in this database yet. It appears once the migration is applied."
          />
        </Card>
      ) : data ? (
        <>
          {data.distribution ? <Distribution dist={data.distribution} data={data} /> : null}
          <div className="mt-4">
            <Form data={data} canManage={canManage} />
          </div>
        </>
      ) : (
        <Card>
          <EmptyState title="No settings" />
        </Card>
      )}
    </AdminPage>
  );
}

/** The live member distribution — how many active riders (km consumed in the
 *  last 6 months) currently sit in each band. */
function Distribution({ dist, data }: { dist: MembershipTierDistribution; data: MembershipTiersResponse }) {
  const cells = [
    { label: 'Base', sub: `< ${data.silver_km} km`, n: dist.base, tone: 'text-slate-300' },
    { label: 'Silver', sub: `≥ ${data.silver_km} km · ${data.silver_pct}%`, n: dist.silver, tone: 'text-slate-100' },
    { label: 'Gold', sub: `≥ ${data.gold_km} km · ${data.gold_pct}%`, n: dist.gold, tone: 'text-amber-300' },
    { label: 'Platinum Black', sub: `≥ ${data.platinum_km} km · ${data.platinum_pct}%`, n: dist.platinum, tone: 'text-violet-300' },
  ];
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      {cells.map((c) => (
        <Card key={c.label}>
          <div className="text-[11px] uppercase tracking-wide text-muted">{c.label}</div>
          <div className={`mt-1 metric text-2xl font-semibold ${c.tone}`}>{c.n.toLocaleString()}</div>
          <div className="mt-0.5 text-[11px] text-muted">{c.sub}</div>
        </Card>
      ))}
    </div>
  );
}

function Form({ data, canManage }: { data: MembershipTiersResponse; canManage: boolean }) {
  const qc = useQueryClient();
  const [enabled, setEnabled] = useState(data.enabled);
  const [silverKm, setSilverKm] = useState(String(data.silver_km));
  const [goldKm, setGoldKm] = useState(String(data.gold_km));
  const [platinumKm, setPlatinumKm] = useState(String(data.platinum_km));
  const [silverPct, setSilverPct] = useState(String(data.silver_pct));
  const [goldPct, setGoldPct] = useState(String(data.gold_pct));
  const [platinumPct, setPlatinumPct] = useState(String(data.platinum_pct));
  const [clientError, setClientError] = useState<string | null>(null);

  useEffect(() => {
    setEnabled(data.enabled);
    setSilverKm(String(data.silver_km));
    setGoldKm(String(data.gold_km));
    setPlatinumKm(String(data.platinum_km));
    setSilverPct(String(data.silver_pct));
    setGoldPct(String(data.gold_pct));
    setPlatinumPct(String(data.platinum_pct));
  }, [data]);

  const mutation = useMutation({
    mutationFn: (input: Parameters<typeof adminApi.updateMembershipTiers>[0]) => adminApi.updateMembershipTiers(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'membership-tiers'] }),
  });

  const onSave = () => {
    setClientError(null);
    const sk = Number(silverKm), gk = Number(goldKm), pk = Number(platinumKm);
    const sp = Number(silverPct), gp = Number(goldPct), pp = Number(platinumPct);
    const nums = [sk, gk, pk, sp, gp, pp];
    if (nums.some((n) => !Number.isFinite(n) || n < 0)) {
      setClientError('All values must be non-negative numbers.');
      return;
    }
    if (!(sk <= gk && gk <= pk)) {
      setClientError('Thresholds must ascend: Silver ≤ Gold ≤ Platinum Black.');
      return;
    }
    if ([sp, gp, pp].some((p) => p > 100)) {
      setClientError('Discounts can’t exceed 100%.');
      return;
    }
    mutation.mutate({
      enabled,
      silver_km: Math.round(sk),
      gold_km: Math.round(gk),
      platinum_km: Math.round(pk),
      silver_pct: sp,
      gold_pct: gp,
      platinum_pct: pp,
    });
  };

  const TierRow = ({
    name,
    km,
    setKm,
    pct,
    setPct,
  }: {
    name: string;
    km: string;
    setKm: (v: string) => void;
    pct: string;
    setPct: (v: string) => void;
  }) => (
    <div className="flex items-center gap-3 border-t border-ink-800 py-3">
      <div className="w-32 text-sm font-medium text-slate-100">{name}</div>
      <label className="flex items-center gap-2 text-xs text-muted">
        ≥
        <input type="number" disabled={!canManage} value={km} onChange={(e) => setKm(e.target.value)} className="input w-24" />
        km consumed (6mo)
      </label>
      <label className="ml-auto flex items-center gap-2 text-xs text-muted">
        <input type="number" disabled={!canManage} value={pct} onChange={(e) => setPct(e.target.value)} className="input w-20" />
        % off top-ups
      </label>
    </div>
  );

  return (
    <Card>
      <label className="flex items-center gap-3 pb-1 text-sm text-slate-200">
        <input
          type="checkbox"
          disabled={!canManage}
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          className="h-4 w-4 rounded border-ink-600 bg-ink-900"
        />
        Tiers enabled — apply the top-up discount (off = tiers still computed/shown to riders, but no discount charged)
      </label>

      <div className="mt-2">
        <TierRow name="Silver" km={silverKm} setKm={setSilverKm} pct={silverPct} setPct={setSilverPct} />
        <TierRow name="Gold" km={goldKm} setKm={setGoldKm} pct={goldPct} setPct={setGoldPct} />
        <TierRow name="Platinum Black" km={platinumKm} setKm={setPlatinumKm} pct={platinumPct} setPct={setPlatinumPct} />
      </div>

      {canManage ? (
        <div className="mt-4 flex items-center gap-3">
          <button type="button" disabled={mutation.isPending} onClick={onSave} className="btn-primary">
            {mutation.isPending ? 'Saving…' : 'Save changes'}
          </button>
          {mutation.isSuccess ? <span className="text-xs text-ok">Saved.</span> : null}
        </div>
      ) : (
        <p className="mt-4 text-xs text-muted">You have read-only access to membership tiers.</p>
      )}
      {clientError ? <p className="mt-2 text-xs text-danger">{clientError}</p> : null}
      {mutation.isError ? <p className="mt-2 text-xs text-danger">{(mutation.error as Error)?.message}</p> : null}
    </Card>
  );
}
